import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const StripeConnectDashboard = () => {
  const { user, token } = useAuth();
  const [accountStatus, setAccountStatus] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [authorizations, setAuthorizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStripeConnectData();
  }, []);

  const loadStripeConnectData = async () => {
    try {
      setLoading(true);
      
      // Load account status
      const accountResponse = await fetch('/api/stripe-connect/account-status', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (accountResponse.ok) {
        const accountData = await accountResponse.json();
        setAccountStatus(accountData.account);
      }

      // Load payment methods
      const methodsResponse = await fetch('/api/stripe-connect/payment-methods', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (methodsResponse.ok) {
        const methodsData = await methodsResponse.json();
        setPaymentMethods(methodsData.paymentMethods);
      }

      // Load authorizations
      const authResponse = await fetch('/api/stripe-connect/authorizations', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (authResponse.ok) {
        const authData = await authResponse.json();
        setAuthorizations(authData.authorizations);
      }

    } catch (error) {
      console.error('Error loading Stripe Connect data:', error);
      setError('Failed to load Stripe Connect data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'restricted': return 'text-red-600 bg-red-100';
      case 'disabled': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPaymentTypeIcon = (type) => {
    switch (type) {
      case 'card': return '💳';
      case 'bank_account': return '🏦';
      case 'sepa_debit': return '🇪🇺';
      case 'bacs_debit': return '🇬🇧';
      default: return '💰';
    }
  };

  const renderAccountStatus = () => {
    if (!accountStatus) {
      return (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center">
            <div className="text-yellow-400 text-xl mr-3">⚠️</div>
            <div>
              <h3 className="text-yellow-800 font-medium">No Stripe Connect Account</h3>
              <p className="text-yellow-700 text-sm">
                You need to set up a Stripe Connect account to receive payments from other users.
              </p>
            </div>
          </div>
          <button
            onClick={() => window.location.href = '/stripe-connect/setup'}
            className="mt-3 bg-yellow-600 text-white px-4 py-2 rounded-md text-sm hover:bg-yellow-700"
          >
            Set Up Now
          </button>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Stripe Connect Account</h3>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(accountStatus.status)}`}>
            {accountStatus.status}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Account ID</p>
            <p className="text-sm font-mono text-gray-900">{accountStatus.stripeAccountId}</p>
          </div>
          
          <div>
            <p className="text-sm text-gray-500">Account Type</p>
            <p className="text-sm text-gray-900 capitalize">{accountStatus.accountType}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Charges Enabled</p>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              accountStatus.chargesEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {accountStatus.chargesEnabled ? '✅ Yes' : '❌ No'}
            </span>
          </div>

          <div>
            <p className="text-sm text-gray-500">Payouts Enabled</p>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              accountStatus.payoutsEnabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {accountStatus.payoutsEnabled ? '✅ Yes' : '❌ No'}
            </span>
          </div>
        </div>

        {accountStatus.status === 'pending' && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
            <p className="text-blue-800 text-sm">
              Your account is being reviewed. You'll receive an email when it's ready.
            </p>
          </div>
        )}

        {!accountStatus.chargesEnabled || !accountStatus.payoutsEnabled ? (
          <div className="mt-4">
            <button
              onClick={() => window.location.href = '/stripe-connect/onboarding'}
              className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm hover:bg-blue-700"
            >
              Complete Onboarding
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const renderPaymentMethods = () => {
    if (paymentMethods.length === 0) {
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-gray-500 text-sm">No payment methods set up yet.</p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Methods</h3>
        <div className="space-y-3">
          {paymentMethods.map((method) => (
            <div key={method.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
              <div className="flex items-center">
                <span className="text-2xl mr-3">{getPaymentTypeIcon(method.paymentType)}</span>
                <div>
                  <p className="font-medium text-gray-900 capitalize">
                    {method.paymentType.replace('_', ' ')}
                  </p>
                  <p className="text-sm text-gray-500">
                    {method.isDefault ? 'Default method' : 'Additional method'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {method.isDefault && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Default
                  </span>
                )}
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  method.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {method.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAuthorizations = () => {
    if (authorizations.length === 0) {
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-gray-500 text-sm">No payment authorizations set up yet.</p>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Authorizations</h3>
        <div className="space-y-3">
          {authorizations.map((auth) => (
            <div key={auth.id} className="p-3 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-900 capitalize">
                  {auth.type.replace('_', ' ')}
                </span>
                <span className="text-sm text-gray-500">
                  {new Date(auth.consentGivenAt).toLocaleDateString()}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Scope</p>
                  <p className="text-gray-900 capitalize">{auth.scope.replace('_', ' ')}</p>
                </div>
                <div>
                  <p className="text-gray-500">Max Amount</p>
                  <p className="text-gray-900">£{auth.maxAmount} {auth.currency}</p>
                </div>
                {auth.expiresAt && (
                  <div className="col-span-2">
                    <p className="text-gray-500">Expires</p>
                    <p className="text-gray-900">{new Date(auth.expiresAt).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAutoApprovalStatus = () => {
    const hasValidAuth = authorizations.some(auth => 
      auth.type === 'invoice_payment' && 
      (!auth.expiresAt || new Date(auth.expiresAt) > new Date())
    );

    const isReady = accountStatus?.chargesEnabled && 
                   accountStatus?.payoutsEnabled && 
                   paymentMethods.length > 0 && 
                   hasValidAuth;

    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">7-Day Auto-Approval Status</h3>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Connect Account Ready</span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              accountStatus?.chargesEnabled && accountStatus?.payoutsEnabled 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {accountStatus?.chargesEnabled && accountStatus?.payoutsEnabled ? '✅ Ready' : '❌ Not Ready'}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Payment Method Set Up</span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              paymentMethods.length > 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {paymentMethods.length > 0 ? '✅ Set Up' : '❌ Not Set Up'}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">Payment Authorization</span>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              hasValidAuth ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {hasValidAuth ? '✅ Authorized' : '❌ Not Authorized'}
            </span>
          </div>
        </div>

        <div className="mt-4 p-3 rounded-md ${
          isReady ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'
        }">
          <p className={`text-sm ${
            isReady ? 'text-green-800' : 'text-yellow-800'
          }`}>
            {isReady 
              ? '🎉 Your account is ready for 7-day auto-approval! Invoices will be automatically approved and payments collected after 7 days.'
              : '⚠️ Your account needs additional setup to enable 7-day auto-approval. Complete the steps above to get started.'
            }
          </p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Stripe Connect Dashboard</h2>
        <button
          onClick={loadStripeConnectData}
          className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm hover:bg-gray-200"
        >
          Refresh
        </button>
      </div>

      {renderAccountStatus()}
      {renderPaymentMethods()}
      {renderAuthorizations()}
      {renderAutoApprovalStatus()}
    </div>
  );
};

export default StripeConnectDashboard;
